import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MapPin, Tag, Users, Shield, ArrowRight, Star, CaretDown, Sparkle, Heart } from "@phosphor-icons/react";
import { changeLanguage, getCurrentLanguage } from "@shared/lib/i18n";
import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { AnimatedDiscountCards } from "@shared/components/AnimatedDiscountCards";
import { PartnersCarousel } from "@shared/components/PartnersCarousel";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

function LandingPage() {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();
  const navigate = useNavigate();
  const landingPageVersion = useQuery(api.admin.getLandingPageVersion);
  const stats = useQuery(api.partners.getLandingPageStats);
  const [isScrolled, setIsScrolled] = useState(false);

  // Redirect către varianta de landing aleasă din admin
  useEffect(() => {
    if (landingPageVersion === "public") {
      navigate({ to: "/public-landing", replace: true });
    } else if (landingPageVersion === "mvp") {
      navigate({ to: "/join", replace: true });
    }
  }, [landingPageVersion, navigate]);

  // Scroll handler - trebuie să fie înainte de orice return condițional
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Dacă încă se încarcă sau e altă variantă, nu afișa nimic (redirect în curs)
  if (
    landingPageVersion === undefined ||
    landingPageVersion === "public" ||
    landingPageVersion === "mvp"
  ) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const toggleLanguage = () => {
    changeLanguage(currentLang === "ro" ? "en" : "ro");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border' : 'bg-transparent'}`}>
        <div className="container mx-auto max-w-6xl px-3 sm:px-0 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center pt-1 gap-2 cursor-pointer">
            <img 
              src="/suuper_logo.svg" 
              alt={t("app.name")}
              className="h-6 sm:h-6 w-auto"
            />
            <span className="text-1xl sm:text-2xl text-text">Iași</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Pe mobil: doar butoanele de auth, language switcher se mută în footer */}
            <Link
              to="/auth/login"
              className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-text-light hover:text-text transition-colors whitespace-nowrap"
            >
              {t("auth:login.title")}
            </Link>

            <Link
              to="/auth/register"
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium bg-primary-gradient text-white rounded-lg hover:shadow-lg hover:shadow-primary/30 transition-all whitespace-nowrap"
            >
              {t("auth:register.title")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/6 via-background to-background" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] bg-primary/20 blur-3xl rounded-full" />

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
            <div className="text-center md:text-left">
            <span className="badge badge-primary mb-4">
              <span className="inline-flex items-center gap-2">
                <Star weight="fill" className="w-4 h-4 text-primary" />
                {currentLang === "ro" ? "Access cu invitație" : "Invite only access"}
              </span>
            </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-text mb-6 tracking-tight">
                {currentLang === "ro" ? (
                  <>Oferte locale,<br/> <span className="text-primary">economii reale</span></>
                ) : (
                  <>
                    <span className="text-primary">Local</span> discounts,<br />
                    <span className="text-primary">frictionless</span>,<br />
                    <span className="text-primary">real</span>.
                  </>
                )}
              </h1>

              <p className="text-lg md:text-xl text-text-light mb-8 max-w-xl mx-auto md:mx-0">
                {currentLang === "ro"
                  ? "Reduceri la restaurante, cafenele, săli de fitness dar și în alte multe locuri din Iași."
                  : "Discounts at restaurants, coffee shops, fitness centers, and more in Iași, right at the venue."}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
                <div>
                  <Link
                    to="/offers-preview"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-primary-gradient text-white rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-primary/30 transition-all shadow-lg shadow-primary/25"
                  >
                    {currentLang === "ro" ? "Vezi reducerile" : "View discounts"}
                    <ArrowRight size={20} />
                  </Link>
                </div>
                <div>
                  <Link
                    to="/auth/login"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-text rounded-xl font-semibold text-lg hover:bg-background-dark transition-colors border border-border"
                  >
                    {t("auth:login.title")}
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl p-6">
                <AnimatedDiscountCards currentLang={currentLang} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Carousel */}
      {stats?.carouselPartners && stats.carouselPartners.length > 0 && (
        <section className="py-12 bg-background">
          <div className="mb-8 px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text">
              {currentLang === "ro" ? "Parteneri locali din Iași" : "Local partners in Iași"}
            </h2>
          </div>
          <PartnersCarousel partners={stats.carouselPartners} />
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Despre experiențe" : "About experiences"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4">
              {currentLang === "ro" ? "Ieși. Explorează. Descoperă." : "Go out. Explore. Discover."}
            </h2>
            <p className="text-text-light mt-4">
              {currentLang === "ro"
                ? "Nu e doar despre reduceri. E despre să vezi locuri noi, să încerci ceva diferit, să ai o ocazie să experimentezi Iași-ul într-un mod nou."
                : "It's not just about discounts. It's about seeing new places, trying something different, having a chance to experience Iași in a new way."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={<Tag size={24} />}
              title={currentLang === "ro" ? "Reduceri exclusive" : "Exclusive discounts"}
              description={
                currentLang === "ro"
                  ? "Până la 50% reducere la partenerii noștri locali"
                  : "Up to 50% off at our local partners"
              }
            />
            <FeatureCard
              icon={<MapPin size={24} />}
              title={currentLang === "ro" ? "Parteneri locali" : "Local partners"}
              description={
                currentLang === "ro"
                  ? "Afaceri locale verificate din Iași"
                  : "Verified local businesses in Iași"
              }
            />
            <FeatureCard
              icon={<Users size={24} />}
              title={currentLang === "ro" ? "Comunitate exclusivă" : "Exclusive community"}
              description={
                currentLang === "ro"
                  ? "Acces doar prin invitație de la membri existenți"
                  : "Access only by invitation from existing members"
              }
            />
            <FeatureCard
              icon={<Shield size={24} />}
              title={currentLang === "ro" ? "Simplu și sigur" : "Simple and secure"}
              description={
                currentLang === "ro"
                  ? "Activezi reducerea direct în locație, fără complicații"
                  : "Activate discount directly at location, no hassle"
              }
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-background-dark">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text mb-12">
            {currentLang === "ro" ? "Cum începi?" : "How do you start?"}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              step={1}
              title={currentLang === "ro" ? "Primește invitația" : "Get invited"}
              description={
                currentLang === "ro"
                  ? "Un prieten care folosește Suuper îți trimite un cod"
                  : "A friend using Suuper sends you a code"
              }
            />
            <StepCard
              step={2}
              title={currentLang === "ro" ? "Explorează ofertele" : "Explore offers"}
              description={
                currentLang === "ro"
                  ? "Vezi locurile din Iași unde poți experimenta ceva nou"
                  : "See places in Iași where you can try something new"
              }
            />
            <StepCard
              step={3}
              title={currentLang === "ro" ? "Activează reducerea" : "Activate discount"}
              description={
                currentLang === "ro"
                  ? "Activezi reducerea în locație. Codul e valabil 60s."
                  : "Activate the discount at the location. Code is valid for 60s."
              }
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Pentru tine" : "For you"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4">
              {currentLang === "ro" ? "De ce să te alături?" : "Why join?"}
            </h2>
            <p className="text-text-light mt-4">
              {currentLang === "ro"
                ? "Mai mult decât reduceri — o ocazie să explorezi Iași-ul într-un mod nou."
                : "More than discounts — a chance to explore Iași in a new way."}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <BenefitCard
              icon={<Sparkle size={24} />}
              title={currentLang === "ro" ? "Explorează locuri noi" : "Explore new places"}
              description={
                currentLang === "ro"
                  ? "Descoperă restaurante, cafenele și locuri pe care nu le-ai încercat încă. Fiecare reducere e o ocazie să vezi ceva diferit."
                  : "Discover restaurants, cafes and places you haven't tried yet. Every discount is a chance to see something different."
              }
            />
            <BenefitCard
              icon={<Heart size={24} />}
              title={currentLang === "ro" ? "Economisește când ieși" : "Save when you go out"}
              description={
                currentLang === "ro"
                  ? "Perfect pentru studenți și tineri. Reduceri reale la locurile unde vrei să mergi, fără complicații."
                  : "Perfect for students and young people. Real discounts at places you want to go, no hassle."
              }
            />
            <BenefitCard
              icon={<Users size={24} />}
              title={currentLang === "ro" ? "Comunitate de oameni ca tine" : "Community of people like you"}
              description={
                currentLang === "ro"
                  ? "Alătură-te unei comunități de studenți și tineri care explorează orașul, descoperă locuri noi și se bucură de experiențe autentice."
                  : "Join a community of students and young people exploring the city, discovering new places and enjoying authentic experiences."
              }
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text mb-12">
            {currentLang === "ro" ? "Ce spun membrii" : "What members say"}
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard
              name="Alexandra M."
              role={currentLang === "ro" ? "Studentă, UMF Iași" : "Student, UMF Iași"}
              text={
                currentLang === "ro"
                  ? "Am descoperit atâtea locuri noi! Înainte mergeam doar la aceleași 2-3 restaurante. Acum explorez Iași-ul și îmi place mult mai mult orașul."
                  : "I discovered so many new places! Before I only went to the same 2-3 restaurants. Now I explore Iași and I like the city much more."
              }
              rating={5}
            />
            <TestimonialCard
              name="Mihai P."
              role={currentLang === "ro" ? "Student, UAIC" : "Student, UAIC"}
              text={
                currentLang === "ro"
                  ? "E perfect pentru studenți. Am o ocazie să ies mai des, să încerc locuri noi fără să mă gândesc la buget. E despre experiențe, nu doar reduceri."
                  : "It's perfect for students. I have a chance to go out more often, try new places without thinking about budget. It's about experiences, not just discounts."
              }
              rating={5}
            />
            <TestimonialCard
              name="Elena D."
              role={currentLang === "ro" ? "Designer, 24 ani" : "Designer, 24 years old"}
              text={
                currentLang === "ro"
                  ? "Mă face să vreau să explorez mai mult orașul. Am găsit cafenele superbe pe care nu le știam, restaurante ascunse. E ca o aventură mică în fiecare weekend."
                  : "It makes me want to explore the city more. I found amazing cafes I didn't know about, hidden restaurants. It's like a little adventure every weekend."
              }
              rating={5}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text mb-12">
            {currentLang === "ro" ? "Întrebări frecvente" : "Frequently asked questions"}
          </h2>

          <div className="space-y-4">
            <FAQItem
              question={currentLang === "ro" ? "Cum obțin un cod de invitație?" : "How do I get an invite code?"}
              answer={
                currentLang === "ro"
                  ? "Codurile de invitație sunt trimise de membrii existenți. Dacă cunoști pe cineva care folosește Suuper, poți cere un cod de la ei."
                  : "Invite codes are sent by existing members. If you know someone using Suuper, you can ask them for a code."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Cât costă?" : "How much does it cost?"}
              answer={
                currentLang === "ro"
                  ? "Suuper funcționează pe bază de invitații. Primești 7 zile trial când inviți un prieten. După aceea, poți alege un plan de membership."
                  : "Suuper works on an invite basis. You get 7 days trial when you invite a friend. After that, you can choose a membership plan."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Cum funcționează QR code-ul?" : "How does the QR code work?"}
              answer={
                currentLang === "ro"
                  ? "Generezi un cod QR în app pentru oferta dorită. Partenerul îl scanează și primești reducerea instant. Codul expiră în 60 de secunde pentru securitate."
                  : "You generate a QR code in the app for the desired offer. The partner scans it and you get the discount instantly. The code expires in 60 seconds for security."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Pot folosi mai multe oferte pe zi?" : "Can I use multiple offers per day?"}
              answer={
                currentLang === "ro"
                  ? "Da, poți folosi mai multe oferte pe zi, în funcție de comerciant și oferta lor. Fiecare partener poate avea propriile reguli și limite. Există un cooldown de 24h între reduceri din aceeași categorie pentru a asigura o distribuție echitabilă."
                  : "Yes, you can use multiple offers per day, depending on the merchant and their offer. Each partner may have their own rules and limits. There's a 24h cooldown between discounts from the same category to ensure fair distribution."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Cum funcționează trial-ul?" : "How does the trial work?"}
              answer={
                currentLang === "ro"
                  ? "Când inviți un prieten care se înregistrează și verifică telefonul, primești 7 zile de trial gratuit. În timpul trial-ului poți folosi toate ofertele disponibile, exact ca un membru cu abonament activ."
                  : "When you invite a friend who registers and verifies their phone, you get 7 days of free trial. During the trial you can use all available offers, just like a member with an active subscription."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Pot folosi ofertele la mai mulți parteneri în aceeași zi?" : "Can I use offers at multiple partners on the same day?"}
              answer={
                currentLang === "ro"
                  ? "Da, poți folosi oferte la diferiți parteneri în aceeași zi. Limita de 3 reduceri pe zi se aplică global, dar poți combina oferte de la restaurante, cafenele, săli de fitness sau alți parteneri."
                  : "Yes, you can use offers at different partners on the same day. The limit of 3 discounts per day applies globally, but you can combine offers from restaurants, cafes, fitness centers or other partners."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Ce se întâmplă dacă QR code-ul expiră?" : "What happens if the QR code expires?"}
              answer={
                currentLang === "ro"
                  ? "QR code-ul expiră după 60 de secunde pentru securitate. Dacă expiră, poți genera unul nou instant în app. Nu există limită de câte ori poți genera un cod pentru aceeași ofertă."
                  : "The QR code expires after 60 seconds for security. If it expires, you can generate a new one instantly in the app. There's no limit to how many times you can generate a code for the same offer."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Ofertele se acumulează cu alte reduceri?" : "Do offers stack with other discounts?"}
              answer={
                currentLang === "ro"
                  ? "Ofertele Suuper sunt valabile conform termenilor fiecărui partener. Unele oferte pot fi combinate cu reduceri existente, altele nu. Verifică întotdeauna detaliile ofertei în app înainte de utilizare."
                  : "Suuper offers are valid according to each partner's terms. Some offers may be combined with existing discounts, others may not. Always check the offer details in the app before use."
              }
            />
            <FAQItem
              question={currentLang === "ro" ? "Cum pot invita prieteni?" : "How can I invite friends?"}
              answer={
                currentLang === "ro"
                  ? "În secțiunea 'Invitații' din app poți vedea codurile tale de invitație. Le poți trimite prin SMS direct din app sau le poți partaja manual. Când un prieten se înregistrează cu codul tău, primești 7 zile trial."
                  : "In the 'Invites' section of the app you can see your invite codes. You can send them via SMS directly from the app or share them manually. When a friend registers with your code, you get 7 days trial."
              }
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-4 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-pattern-lines opacity-20" />
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] bg-white/10 blur-3xl rounded-full" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] bg-white/10 blur-3xl rounded-full" />
        <div className="container mx-auto max-w-3xl text-center relative z-10">
          <h2 className="text-3xl font-bold text-white mb-6">
            {currentLang === "ro"
              ? "Ai un cod de invitație?"
              : "Have an invite code?"}
          </h2>
          <p className="text-lg text-white/80 mb-8">
            {currentLang === "ro"
              ? "Înregistrează-te acum și începe să economisești la fiecare achiziție."
              : "Sign up now and start saving on every purchase."}
          </p>
          <div>
            <Link
              to="/auth/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary rounded-xl font-semibold text-lg hover:bg-background transition-colors"
            >
              {t("auth:register.submit")}
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-text">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Link to="/partners" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <MapPin size={20} className="text-white" />
                </div>
                <span className="text-lg font-bold text-white">
                  {t("app.name")}
                </span>
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-6 text-white/60 text-sm">
                <a href="#" className="hover:text-white transition-colors">
                  {currentLang === "ro" ? "Termeni și condiții" : "Terms of Service"}
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  {currentLang === "ro" ? "Confidențialitate" : "Privacy Policy"}
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  {currentLang === "ro" ? "Contact" : "Contact"}
                </a>
              </div>

              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className="px-3 py-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors rounded-md hover:bg-white/10 border border-white/20"
              >
                {currentLang === "ro" ? "EN" : "RO"}
              </button>
            </div>

            <p className="text-white/40 text-sm text-center sm:text-left">
              © {new Date().getFullYear()} Suuper
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Componenta pentru carduri feature
function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-4 p-6 rounded-xl bg-white border border-border shadow-sm hover:shadow-lg transition-shadow cursor-pointer text-left">
      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-text mb-2">{title}</h3>
        <p className="text-text-light text-sm">{description}</p>
      </div>
    </div>
  );
}

// Componenta pentru pași
function StepCard({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center text-center p-6 rounded-xl bg-white/90 border border-border shadow-sm hover:shadow-lg transition-shadow">
      <div className="relative mb-4">
        <div className="w-16 h-16 bg-primary-gradient rounded-full flex items-center justify-center text-white text-xl font-bold mb-2 shadow-primary">
          {step}
        </div>
      </div>
      <h3 className="text-lg font-semibold text-text mb-2">{title}</h3>
      <p className="text-text-light text-sm">{description}</p>
    </div>
  );
}

// Componenta pentru beneficii
function BenefitCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-4 p-6 rounded-xl bg-white border border-border shadow-sm hover:shadow-lg transition-shadow text-left">
      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-text mb-2">{title}</h3>
        <p className="text-text-light text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// Componenta pentru testimoniale
function TestimonialCard({
  name,
  role,
  text,
  rating,
}: {
  name: string;
  role: string;
  text: string;
  rating: number;
}) {
  return (
    <div className="p-6 rounded-xl bg-white border border-border shadow-sm hover:shadow-lg transition-shadow">
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} size={16} className="fill-success text-success" />
        ))}
      </div>
      <p className="text-text-light mb-4 text-sm leading-relaxed">"{text}"</p>
      <div>
        <div className="font-semibold text-text">{name}</div>
        <div className="text-sm text-text-muted">{role}</div>
      </div>
    </div>
  );
}

// Componenta pentru FAQ
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-background-dark transition-colors"
      >
        <span className="font-semibold text-text">{question}</span>
        <div
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
        >
          <CaretDown size={20} className="text-text-muted" />
        </div>
      </button>
      <div
        style={{ height: isOpen ? "auto" : 0, overflow: "hidden", transition: "height 0.2s" }}
      >
        <div className="px-6 pb-4 text-text-light text-sm">{answer}</div>
      </div>
    </div>
  );
}

